// =============================================================================
//  Copyright : 2023 semantics GmbH
//  Date      : $Date: 2025-12-16 09:38:23 +0100 (Tue, 16 Dec 2025) $
//  Author    : Olli Powroznik
//  Worker    : $Author: o.powroznik $
//  Revision  : $Rev: 159231 $
//  Purpose   : custom buttons
// =============================================================================
//
// ---Third-party imports
import React, {useRef, useState, useEffect} from 'react'
import {Loader} from 'semantic-ui-react'
import clsx from 'clsx'

// ---Framework imports
import log from 'uix/logging'
import {Button} from 'uix/components'
import {Sel} from 'uix/registries'
import {useDelay, useInView, useSelector} from 'uix/hooks'

// ---Local imports
import S from './mediaplayer.scss'
import {AudioWaves} from './index'

const getTypeFromSrc = src => {
  if (!src) return null

  // Extract file extension from URL or filename
  const extension = src
    .split('.')
    .pop()
    .toLowerCase()
    .split('?')[0]

  const mimeTypeMap = {
    // Audio formats
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    ogg: 'audio/ogg',
    aac: 'audio/aac',
    flac: 'audio/flac',
    m4a: 'audio/mp4',
    wma: 'audio/x-ms-wma',
    webm: 'audio/webm',

    // Video formats
    mp4: 'video/mp4',
    avi: 'video/x-msvideo',
    mov: 'video/quicktime',
    wmv: 'video/x-ms-wmv',
    flv: 'video/x-flv',
    mkv: 'video/x-matroska',
    webm: 'video/webm',
    ogv: 'video/ogg',
    m4v: 'video/mp4',
  }

  return mimeTypeMap[extension] || null
}

const MediaPlayer = ({mode, src, ...props}) => {
  let mediaRef = useRef()

  let [inViewRef, inView] = useInView({triggerOnce: false, threshold: 0.1})
  let [playerState, setPlayerState] = useState({
    muted: false,
    playing: false,
    ended: false,
    paused: false,
    progress: 0,
    duration: 0,
    currentTime: 0,
  })

  let {muted, playing, ended, duration, currentTime} = playerState
  let setPlayer = props => {
    return setPlayerState({...playerState, ...props})
  }

  // calculates percentage for progressbar
  const progressOnTimeUpdate = () => {
    const {currentTime, duration} = mediaRef?.current
    const progressPercentage = (currentTime / duration) * 100
    setPlayer({progress: progressPercentage, duration: duration, currentTime: currentTime})
  }

  // media sources

  // Auto-determine if this is embedded content (external URL)
  let isEmbedded = src && (src.startsWith('http://') || src.startsWith('https://'))
  let mediaType = getTypeFromSrc(src)

  //resets player after full playback
  useDelay(
    () => {
      let ref = mediaRef?.current
      if (ref) {
        ref.currentTime = 0
      }
      setPlayer({currentTime: 0, playing: false, progress: 0, ended: false, loaded: false})
    },
    [1500],
    [ended]
  )

  useEffect(() => {
    let ref = mediaRef?.current
    if (ref) {
      ref.muted = muted
    }
  }, [muted])

  // player event handler
  const endHandler = () => {
    let ref = mediaRef?.current
    if (ref) {
      setPlayer({ended: true})
    }
  }

  const muteHandler = () => {
    setPlayer({muted: !muted})
  }

  const loadHandler = () => {
    let ref = mediaRef?.current
    setPlayer({duration: ref.duration, loaded: true})
  }

  const playAudio = () => {
    setPlayer({playing: true, paused: false})
    mediaRef?.current.play()
  }

  const pauseAudio = () => {
    setPlayer({paused: true, playing: false})
    mediaRef?.current.pause()
  }

  const playerHandler = () => {
    playing ? pauseAudio() : playAudio()
  }

  //!TODO: datefn
  // calculate remaining time on playback
  let remainingTime = new Date(0).toISOString().substr(11, 8)
  if (duration) {
    remainingTime = new Date((duration - currentTime) * 1000).toISOString().substr(11, 8)
  }

  if (mode === 'audio') {
    return (
      <AudioPlayer
        endHandler={endHandler}
        inView={inView}
        inViewRef={inViewRef}
        loadHandler={loadHandler}
        mediaRef={mediaRef}
        muteHandler={muteHandler}
        playerHandler={playerHandler}
        playerState={playerState}
        progressOnTimeUpdate={progressOnTimeUpdate}
        remainingTime={remainingTime}
        setPlayer={setPlayer}
        src={src}
        type={mediaType}
        {...props}
      />
    )
  } else {
    return (
      <VideoPlayer
        isEmbedded={isEmbedded}
        endHandler={endHandler}
        inView={inView}
        inViewRef={inViewRef}
        loadHandler={loadHandler}
        mediaRef={mediaRef}
        playerHandler={playerHandler}
        playerState={playerState}
        progressOnTimeUpdate={progressOnTimeUpdate}
        remainingTime={remainingTime}
        setPlayer={setPlayer}
        src={src}
        type={mediaType}
        {...props}
      />
    )
  }
}

const ProgressBar = ({playerState, setPlayer, mediaRef}) => {
  let progressRef = useRef()
  let {duration, progress} = playerState
  // progressbar click changes playback position
  const handleClickProgressBar = e => {
    let clickPosition = e.pageX - progressRef.current.getBoundingClientRect().x
    let width = progressRef.current.offsetWidth
    let progressPercentage = (clickPosition / width) * 100
    let updatedCurrentTime = (duration / 100) * progressPercentage
    mediaRef.current.currentTime = updatedCurrentTime
    setPlayer({progress: progressPercentage, currentTime: updatedCurrentTime})
  }

  return (
    <div onClick={e => handleClickProgressBar(e)} ref={progressRef} className={S.progressBarCnt}>
      <div className={S.progressBar} style={{width: `${progress}%`}}></div>
    </div>
  )
}

const AudioPlayer = ({
  caption,
  endHandler,
  inView,
  inViewRef,
  loadHandler,
  mediaRef,
  muteHandler,
  playerHandler,
  playerState,
  preload,
  progressOnTimeUpdate,
  remainingTime,
  setPlayer,
  src,
  type,
}) => {
  let [sceneRef, sceneInView] = useInView({triggerOnce: false})
  let [sticky, setSticky] = useState(false)
  let {muted, playing, currentTime} = playerState
  //adds sticky-player when playing or already played
  useEffect(() => {
    let ref = mediaRef?.current
    if (ref && !sceneInView && !inView) {
      setSticky(false)
      ref.pause()
      setPlayer({paused: true, playing: false})
    }
    if (inView) {
      setSticky(false)
    }
    if (!sticky) {
      if (sceneInView && !inView && (currentTime || playerState.playing)) {
        setSticky(true)
      }
    }
  }, [inView, sceneInView])

  let progressBar = (
    <ProgressBar mediaRef={mediaRef} setPlayer={setPlayer} playerState={playerState} />
  )

  return (
    <>
      <div className={S.sceneHeight} ref={sceneRef} />
      <div className={S.visibleAnchor} ref={inViewRef} />
      <div className={clsx(S.playerContainer, sticky && S.sticky)}>
        <div className={S.audioPlayer}>
          <div className={S.center}>
            <Button
              className={S.playButton}
              style={{
                flex: 'none',
                color: 'var(--presentation_themeColor)',
                border: '1px solid var(--presentation_themeColor)',
              }}
              inverted
              icon={playing ? 'pause' : 'play'}
              onClick={playerHandler}
            />
            {caption ? <div className={S.caption}>{caption}</div> : progressBar}
            <div className={S.audioWavesCnt}>
              <AudioWaves
                className={S.videoWaves}
                hidden={false}
                onClick={muteHandler}
                active={playing}
                muted={muted}
              />
            </div>
          </div>
          {caption && progressBar}
          <div className={S.remaining}>{remainingTime}</div>
          <audio
            onLoadedData={loadHandler}
            onEnded={endHandler}
            onTimeUpdate={progressOnTimeUpdate}
            ref={mediaRef}
            preload={preload}
          >
            <source src={src} type={type || 'audio/mpeg'} />
          </audio>
        </div>
      </div>
    </>
  )
}

const VideoPlayer = ({
  caption,
  consent = true,
  isEmbedded,
  endHandler,
  height = '420px',
  id,
  inView,
  inViewRef,
  loadHandler,
  mediaRef,
  playerHandler,
  playerState,
  poster,
  preload = 'Metadata',
  // preload = 'Metadata' would be the better way to go
  // problem: poster image disappearing after load - use 'none' when poster is provided
  progressOnTimeUpdate,
  remainingTime,
  setPlayer,
  src,
  type,
}) => {
  let apiurl = useSelector(Sel.apiurl)
  let i18n = useSelector(Sel.i18n)
  let [consentGiven, setConsentGiven] = useState(false)
  let [hovered, setHovered] = useState(false)
  let {playing, currentTime, paused, loaded} = playerState

  // Handle poster URL - if poster is id, use apiurl.href, otherwise use URL
  let videoPoster
  if (poster && !isEmbedded) {
    if (!isNaN(poster)) {
      videoPoster = apiurl.href(`/download/webresource/${poster}`) // Use API URL for numeric IDs
    } else {
      videoPoster = poster // Use direct URL for non-numeric values
    }
  } else {
    videoPoster = undefined
  }

  // Use 'none' preload when poster is provided to keep poster visible
  let videoPreload = poster && !isEmbedded ? 'none' : preload

  useEffect(() => {
    if (!inView) {
      let ref = mediaRef?.current
      if (ref && !isEmbedded) {
        ref.pause()
        setPlayer({paused: true, playing: false})
      }
    }
  }, [inView])

  const toggleFullscreen = () => {
    let el = document.getElementById(id)
    if (el.requestFullscreen) {
      el.requestFullscreen()
    } else if (el.msRequestFullscreen) {
      el.msRequestFullscreen()
    } else if (el.mozRequestFullScreen) {
      el.mozRequestFullScreen()
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen()
    }
  }

  if (!isEmbedded) {
    src = apiurl.href(src)
  } // else external url (probably youtube)

  let hideControls = !loaded || !hovered

  let onCanPlayHandler = () => {}

  return (
    <>
      <div
        ref={inViewRef}
        className={clsx(S.videoPlayer, isEmbedded && S.embeddedPlayer)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {!isEmbedded ? (
          <>
            {/* {loaded ? ( */}
            <Button
              style={{
                color: 'var(--presentation_themeColor)',
                border: '1px solid var(--presentation_themeColor)',
              }}
              className={clsx(S.playButton, playing && S.hide)}
              inverted
              size="huge"
              icon={paused || currentTime == 0 ? 'play' : 'pause'}
              onClick={playerHandler}
            />
            {/*)
            <Loader
              active
              inline="centered"
              size="massive"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            />
           )} */}
            <video
              id={id}
              onClick={playerHandler}
              width="100%"
              height="100%"
              onCanPlay={onCanPlayHandler}
              // onLoadedData={loadHandler}
              onEnded={endHandler}
              onTimeUpdate={progressOnTimeUpdate}
              ref={mediaRef}
              preload={videoPreload}
              poster={videoPoster}
            >
              <source src={src} type={type || 'video/mp4'} />
            </video>
            <div className={clsx(S.bottomCnt, S.bottomCnt, !hovered && S.hide)}>
              <Button
                className={S.fullscreenButton}
                inverted
                style={{
                  position: 'absolute',
                  top: '-1rem',
                  right: '-.5rem',
                  background: 'transparent',
                }}
                icon="expand"
                onClick={toggleFullscreen}
              />
              <div className={S.captions}>
                <div className={S.remaining}>{remainingTime}</div>
              </div>
              <ProgressBar mediaRef={mediaRef} setPlayer={setPlayer} playerState={playerState} />
            </div>
          </>
        ) : (
          <div>
            {!consent || consentGiven ? (
              <iframe
                frameBorder="0"
                ref={mediaRef}
                src={src}
                wmode="transparent"
                type="video/mp4"
                width="100%"
                height={height}
                allowFullScreen
              />
            ) : (
              <ConsentOverlay setConsent={setConsentGiven} height={height} i18n={i18n} />
            )}
          </div>
        )}
      </div>
      <div style={{fontSize: '0.85em'}}>
        {caption && !isEmbedded && <div className={S.caption}>{caption}</div>}
      </div>
    </>
  )
}

const ConsentOverlay = ({height, setConsent, i18n}) => {
  return (
    <>
      <div className={S.consentOverlay} style={{height: height}}>
        <Button
          disabled
          style={{
            flex: 'none',
            color: 'var(--presentation_themeColor)',
            border: '1px solid var(--presentation_themeColor)',
          }}
          inverted
          size="giant"
          icon="play"
        />
        <div className={S.content}>
          <p>{i18n.get('other.videoConsent')}</p>
          <Button
            size="small"
            inverted
            bordered
            content="Akzeptieren und Video laden"
            circle={false}
            onClick={() => setConsent(true)}
          />
        </div>
      </div>
    </>
  )
}

MediaPlayer.Audio = ({...props}) => <MediaPlayer mode="audio" {...props} />
MediaPlayer.Video = ({...props}) => <MediaPlayer mode="video" {...props} />
export {MediaPlayer}
